import { apiRequest } from '../../lib/api';
import type {
  BonusResult,
  BuyInResult,
  BuyInType,
  PrizeResult,
  PrizeSource,
  PubBonus,
  PubBuyIn,
  PubCustomer,
  PubDashboard,
  PubHistoryRow,
  PubPrize,
  PubTournament,
  TournamentResultSummary,
} from './types';

interface Rows<T> {
  rows: T[];
}

function post<T>(path: string, payload: unknown): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', idempotent: true, body: JSON.stringify(payload) });
}

export const pubAdminApi = {
  dashboard: () => apiRequest<PubDashboard>('/api/v1/pub/dashboard'),
  customers: () => apiRequest<Rows<PubCustomer>>('/api/v1/pub/customers').then((r) => r.rows),
  buyIns: () => apiRequest<Rows<PubBuyIn>>('/api/v1/pub/buy-ins').then((r) => r.rows),
  prizes: () => apiRequest<Rows<PubPrize>>('/api/v1/pub/prizes').then((r) => r.rows),
  bonuses: () => apiRequest<Rows<PubBonus>>('/api/v1/pub/bonuses').then((r) => r.rows),
  tournaments: () => apiRequest<Rows<PubTournament>>('/api/v1/pub/tournaments').then((r) => r.rows),
  history: () => apiRequest<Rows<PubHistoryRow>>('/api/v1/pub/history').then((r) => r.rows),

  processBuyIn: (input: { customerId: string; transactionType: BuyInType; amount: number }) =>
    post<BuyInResult>('/api/v1/pub/buy-ins', {
      customer_id: input.customerId,
      transaction_type: input.transactionType,
      amount: input.amount,
    }),
  awardPrize: (input: { customerId: string; amount: number; source: PrizeSource; tournamentId: string | null }) =>
    post<PrizeResult>('/api/v1/pub/prizes', {
      customer_id: input.customerId,
      amount: input.amount,
      source: input.source,
      tournament_id: input.source === 'tournament' ? input.tournamentId : null,
    }),
  grantBonus: (input: { customerId: string; amount: number; reason: string }) =>
    post<BonusResult>('/api/v1/pub/bonuses', {
      customer_id: input.customerId,
      amount: input.amount,
      reason: input.reason.trim(),
    }),
  cancelBonus: (input: { id: string; reason: string }) =>
    post<{ id: string; status: 'cancelled'; cancelled_at: string }>(`/api/v1/pub/bonuses/${input.id}/cancel`, {
      reason: input.reason.trim(),
    }),
  createTournament: (input: { name: string; startTime: string }) =>
    post<PubTournament>('/api/v1/pub/tournaments', { name: input.name.trim(), start_time: input.startTime }),
  completeTournament: (id: string) =>
    post<{ id: string; status: 'completed'; end_time: string; version: number }>(`/api/v1/pub/tournaments/${id}/complete`, {}),
  saveTournamentResults: (input: { id: string; prizes: Record<string, number>; reason: string }) =>
    apiRequest<TournamentResultSummary>(`/api/v1/pub/tournaments/${input.id}/results`, {
      method: 'PATCH',
      idempotent: true,
      body: JSON.stringify({ prizes: input.prizes, reason: input.reason.trim() }),
    }),
};
